/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { IconDownload } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ListView from "../../../components/common/ListView";
import PremiumSearch from "../../../components/common/PremiumSearch";
import * as XLSX from "xlsx";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useUpdateProductItemStatus } from "../../../apis/productApis";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { useFetchProductItemInventoryData } from "../../../hooks/inventoryHooks";
import formatDate from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../../features/auth/authSlice";

interface ItemInventoryProps {
  warehouseId?: string;
  searchQuery?: string;
  portalContainer?: HTMLDivElement | null;
  headerSubTabs?: React.ReactNode;
}

export default function ItemInventory({
  warehouseId: propWarehouseId,
  searchQuery,
  portalContainer,
  headerSubTabs,
}: ItemInventoryProps = {}) {
  const { t } = useLanguage();
  const profile = useSelector((state: any) => state.auth?.profile);
  const warehouseId = propWarehouseId || profile?.warehouse;

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const { allowed: canUpdateProduct } = usePermission("products", "update");
  const permissions = useSelector(selectClientPermissions);
  const { allowed: canExportProductInventory } = usePermission(
    "product_inventory",
    "export",
  );
  const showExport =
    !permissions || permissions.length === 0 || canExportProductInventory;
  const [localSearch, setLocalSearch] = useState<string>("");

  const searchFor = searchQuery !== undefined ? searchQuery : localSearch;
  const setSearchFor = searchQuery !== undefined ? () => {} : setLocalSearch;
  const debouncedSearchFor = useDebounce(searchFor, 550);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);

  const { mutate: updateProductStatus, isPending: statusPending } =
    useUpdateProductItemStatus();

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = async () => {
    if (!canUpdateProduct) {
      toast.error(t("You do not have permission to update items."));
      return;
    }
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateProductStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Status updated successfully"));
          setActionModal(false);
        },
        onError: (error) => {
          toast.error(t(`${error}`));
        },
      },
    );
  };

  const { data, isLoading, refetch } = useFetchProductItemInventoryData(
    pageNo,
    limit,
    debouncedSearchFor,
    undefined,
    warehouseId,
  );

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor, warehouseId]);

  useEffect(() => {
    setPageNo(1);
  }, [warehouseId]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const handleExportXLSX = () => {
    const dataToExport = data?.inventory || [];
    if (dataToExport.length === 0) {
      toast.error(t("No data available to export"));
      return;
    }
    const exportData = dataToExport.map((row: any, idx: number) => ({
      [t("#")]: idx + 1,
      [t("Item Type")]: row.storage !== "BIN" ? "B2C" : "B2B",
      [t("Location")]: row.locationName || "",
      [t("Item Name")]: row.itemName || "",
      [t("SKU")]: row.sku || "",
      [t("Storage Type")]: row.locationType || "BIN",
      [t("Capacity")]: row.capacity || "---",
      [t("Avl. Qty.")]: row.quantity ?? "-",
      [t("Tag")]: row.isReserved ? t("Reserved") : t("Received"),
      [t("Expiry Date")]: row.expiryDate || "",
      [t("Created At")]: row.createdAt
        ? new Date(row.createdAt).toLocaleString()
        : "",
      [t("Last Updated")]: row.updatedAt
        ? new Date(row.updatedAt).toLocaleString()
        : "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("Item Inventory"));
    XLSX.writeFile(wb, "item_inventory_export.xlsx");
    toast.success(t("Exported successfully!"));
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "Tag", value: "tag", isShow: true },
    { text: "Item", value: "itemName", isShow: true },
    { text: "Bin Location", value: "shelveID", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    // { text: "Merchant", value: "merchant", isShow: true },
    { text: "Warehouse", value: "warehouse", isShow: true },
    { text: "Storage Type", value: "storageType", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "Expiry", value: "expiry", isShow: true },
    { text: "Created At", value: "createdAt", isShow: true },
    { text: "Last Updated", value: "updatedAt", isShow: true },
    { text: "Updated By", value: "updatedBy", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: t("#"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "tag":
          return {
            label: t("Tag"),
            render: (c: any) => {
              const type = c.isReserved ? t("Reserved") : t("Inbound");
              return (
                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-slate-100 text-slate-800 border-slate-200">
                  {type}
                </span>
              );
            },
          };
        case "itemName":
          return {
            label: t("Items"),
            render: (c: any) => (
              <div className="flex items-center justify-start space-x-1.5 truncate max-w-xs">
                <img
                  alt={c.itemName}
                  src={c.image || "/img/product-placeholder.png"}
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = "none";
                  }}
                  className="size-8 rounded-md object-cover object-center shrink-0 border border-slate-200 bg-slate-50"
                />
                <span title={c.itemName} className="truncate text-xs font-medium text-slate-800">
                  {c.itemName}
                </span>
              </div>
            ),
          };
        case "shelveID":
          return {
            label: t("Location"),
            render: (c: any) => (
              <p
                title={c.itemName}
                className="text-left text-[11px] font-extrabold text-[#212121]/70"
              >
                {c.locationName ? c.locationName : ""}
              </p>
            ),
          };
        case "sku":
          return {
            label: t("SKU"),
            render: (c: any) => (
              <p
                title={c.sku}
                className="text-left text-[11px] font-extrabold text-[#212121]/70 truncate max-w-[120px]"
              >
                {c.sku ? c.sku : ""}
              </p>
            ),
          };
        // case "merchant":
        //   return {
        //     label: t("Merchant"),
        //     render: (c: any) => c.clientName || c.merchantName || "",
        //   };
        case "warehouse":
          return {
            label: t("Warehouse"),
            render: (c: any) => c.warehouseName || "",
          };
        case "storageType":
          return {
            label: t("Storage"),
            render: (c: any) => c.locationType || "Bin",
          };
        case "capacity":
          return {
            label: t("Quantity"),
            render: (c: any) => {
              const capacity = Number(c.capacity) || 0;
              const quantity = Number(c.quantity) || 0;
              const percentage =
                capacity > 0 ? Math.min((quantity / capacity) * 100, 100) : 0;
              return (
                <div className="flex justify-center items-center">
                  <div className="w-24">
                    <div className="flex justify-between items-center px-1">
                      <span className="text-xs font-semibold text-[#212121]/70">
                        {c.capacity} / {c.quantity}
                      </span>
                      <span className="text-[10px] font-semibold text-[#212121]/70">
                        {Math.round(percentage)}%
                      </span>
                    </div>
                    <div className="h-1 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-green-500 transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            },
          };
        case "expiry":
          return {
            label: t("Expiry"),
            className: "text-center",
            headerClassName: "text-center",
            children: [
              {
                label: t("Status"),
                className: "text-center",
                headerClassName: "text-center",
                render: (c: any) => {
                  if (!c?.expiryDate)
                    return <div style={{ color: "gray" }}>{t("No")}</div>;
                  const now = new Date();
                  const expiry = new Date(c.expiryDate);
                  expiry.setHours(23, 59, 59, 999);
                  const diffMs = expiry.getTime() - now.getTime();
                  if (diffMs <= 0) {
                    return <div className="text-red-500 font-medium">{t("Yes")}</div>;
                  }
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  if (diffHours < 24) {
                    return (
                      <div className="text-red-500 font-medium">
                        {t("Expires in")} {diffHours} {t("hours")}
                      </div>
                    );
                  }
                  if (diffDays <= 7) {
                    return (
                      <div className="text-amber-500 font-medium">
                        {t("Expires in")} {diffDays} {t("days")}
                      </div>
                    );
                  }
                  return (
                    <div style={{ color: "green" }}>
                      {t("Active")} ({diffDays} {t("days")})
                    </div>
                  );
                },
              },
              {
                label: t("Date"),
                className: "text-center",
                headerClassName: "text-center",
                render: (c: any) => {
                  if (!c?.expiryDate) return <div></div>;
                  return <div>{formatDate(c.expiryDate)}</div>;
                },
              },
            ],
          };
        case "createdAt":
          return {
            label: t("Created At"),
            render: (c: any) => formatDate(c.createdAt),
          };
        case "updatedAt":
          return {
            label: t("Last Updated"),
            render: (c: any) => formatDate(c.updatedAt),
          };
        case "updatedBy":
          return {
            label: t("Updated By"),
            render: (c: any) => c.updatedBy || "",
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const actionControls = (
    <>
      {searchQuery === undefined && (
        <PremiumSearch
          value={searchFor}
          placeholder={t("Search by name, SKU...")}
          onChange={(e) => {
            setSearchFor(e.target.value);
            setPageNo(1);
          }}
        />
      )}

      {showExport && (
        <button
          onClick={handleExportXLSX}
          className="flex items-center justify-center p-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 h-8 w-8 cursor-pointer"
          title={t("Export")}
        >
          <IconDownload size={17} stroke={2.5} />
        </button>
      )}

      {data && data.totalPages * limit > 10 && (
        <EntriesSelector
          value={limit}
          options={[
            10,
            ...(data.totalPages * limit > 10 ? [20] : []),
            ...(data.totalPages * limit > 20 ? [30] : []),
            ...(data.totalPages * limit > 50 ? [50] : []),
            ...(data.totalPages * limit > 100 ? [100] : []),
          ]}
          onChange={(val) => {
            setLimit(val);
            setPageNo(1);
          }}
        />
      )}

      <ColumnsFilter
        columnsData={columnsData.map((c) => ({ ...c, text: t(c.text) }))}
        setHeadings={setHeadings}
      />
    </>
  );

  return (
    <div>
      {portalContainer
        ? createPortal(actionControls, portalContainer)
        : !headerSubTabs && (
            <div className="flex justify-end items-center mb-3 gap-2.5">{actionControls}</div>
          )}

      <div className="rounded-r-3xl border-b-3xl border border-slate-100 shadow-sm overflow-visible">
        {/* Table Container (Joined) */}
        <div className="">
          {isLoading ? (
            <TableSkeleton
              columns={columnsData
                .filter((c) => c.isShow)
                .map((c) => t(c.text))}
              rows={6}
            />
          ) : data?.inventory?.length ? (
            <ListView
              tdClassName="rounded-r-none rounded-l-none "
              mainDivClassName="bg-transparent rounded-b-3xl"
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent rounded-b-3xl"
              thClassName="border-t"
              data={data?.inventory}
              columns={columns}
            />
          ) : (
            <NoDataFound />
          )}
        </div>
      </div>

      <div className="flex justify-center items-center py-4">
        {data?.totalPages > 1 && (
          <Pagination
            currentPage={pageNo}
            totalPages={data?.totalPages}
            fetchData={handlePaginationFetch}
          />
        )}
      </div>

      {actionModal && (
        <MessageModal
          open={actionModal}
          onClose={() => setActionModal(false)}
          actionButtonOnClick={confirmUpdateStatus}
          title={t("Update Status")}
          subtitle={t("Are you sure you want to change this item status?")}
        />
      )}
    </div>
  );
}
